import * as React from 'react';
import { useTranslation } from 'react-i18next';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import { Currency } from '@etiquetachef/types';
import { KpiCard } from './KpiCard';
import {
  formatCurrencyCents,
  formatInt,
  formatPct,
  formatUsdCents,
} from './formatters';
import type { Anomaly } from './useAnomaly';
import type { FinanceMetrics } from './useFinanceMetrics';

export type KpiCardsProps = {
  metrics: FinanceMetrics;
  anomalies: readonly Anomaly[];
};

/** Texto da primeira anomalia do `kind` dado, ou `null` (sem selo no card). */
function anomalyDetailFor(
  anomalies: readonly Anomaly[],
  kind: Anomaly['kind'],
): string | null {
  return anomalies.find((a) => a.kind === kind)?.detail ?? null;
}

/**
 * Grade de 10 KPIs do `/dashboard/finance` (MRR por moeda, ARPT, vendas, membros, etiquetas,
 * net new MRR, churn), com delta vs. período anterior e selo de anomalia.
 *
 * Origem: sem issue — commit 498e432c6, 2026-05-04 15:52 +0200 (rota /dashboard/finance).
 *
 * Contrato: só apresentação — recebe `FinanceMetrics` (todo valor monetário em CENTAVOS, sufixo
 * `Cents`; deltas em fração) e a lista de `Anomaly` de `useAnomaly`; a divisão por 100 acontece
 * nos `formatters`. `labelsPrinted == null` vira "—" (métrica ainda sem dado) e
 * `labelsHasPreDeployGap` troca o helper do card de etiquetas. Anomalia é casada por `kind`
 * (`MRR_DROP`, `SALES_DROP`, `NET_NEW_NEGATIVE_3D`) e vira `anomalyDetail` do card.
 *
 * Usado por: `FinanceDashboardPage` (web `apps/admin`, super-admin).
 *
 * Arquitetura: a régua dos números é de `useFinanceMetrics` (média do período sobre
 * `/aggregations/daily`); este componente não calcula nada — mudar um KPI começa lá.
 *
 * Histórico:
 *  - 2026-06-09 00:40 +0200 — (39-G) Tailwind/shadcn → Cloudscape `ColumnLayout` + `KpiCard`.
 */
export function KpiCards({ metrics, anomalies }: KpiCardsProps): React.JSX.Element {
  const { t } = useTranslation();
  const labelsValue =
    metrics.labelsPrinted == null ? '—' : formatInt(metrics.labelsPrinted);
  const labelsHelper = metrics.labelsHasPreDeployGap
    ? t('finance.kpis.labelsPreDeployHelper')
    : null;

  return (
    <ColumnLayout columns={4}>
      <KpiCard
        label={t('finance.kpis.mrrUsd')}
        value={formatUsdCents(metrics.mrrUsdAvgCents)}
        deltaPct={metrics.deltas.mrrUsd}
        anomalyDetail={anomalyDetailFor(anomalies, 'MRR_DROP')}
      />
      <KpiCard
        label={t('finance.kpis.mrrBrl')}
        value={formatCurrencyCents(metrics.mrrByCurrencyAvgCents.BRL, Currency.BRL)}
      />
      <KpiCard
        label={t('finance.kpis.mrrUsdNative')}
        value={formatCurrencyCents(metrics.mrrByCurrencyAvgCents.USD, Currency.USD)}
      />
      <KpiCard
        label={t('finance.kpis.mrrEur')}
        value={formatCurrencyCents(metrics.mrrByCurrencyAvgCents.EUR, Currency.EUR)}
      />
      <KpiCard
        label={t('finance.kpis.arpt')}
        value={formatUsdCents(metrics.arptUsdAvgCents)}
        deltaPct={metrics.deltas.arptUsd}
      />
      <KpiCard
        label={t('finance.kpis.newTenants')}
        value={formatInt(metrics.newTenants)}
        deltaPct={metrics.deltas.newTenants}
        anomalyDetail={anomalyDetailFor(anomalies, 'SALES_DROP')}
      />
      <KpiCard
        label={t('finance.kpis.newMembers')}
        value={formatInt(metrics.newMembers)}
        deltaPct={metrics.deltas.newMembers}
      />
      <KpiCard
        label={t('finance.kpis.labels')}
        value={labelsValue}
        helperText={labelsHelper}
        deltaPct={metrics.deltas.labels}
      />
      <KpiCard
        label={t('finance.kpis.netNewMrr')}
        value={formatUsdCents(metrics.netNewMrrUsdCents)}
        deltaPct={metrics.deltas.netNewMrrUsd}
        anomalyDetail={anomalyDetailFor(anomalies, 'NET_NEW_NEGATIVE_3D')}
      />
      <KpiCard
        label={t('finance.kpis.churn')}
        value={`${formatInt(metrics.churnedTenants)} · ${formatPct(metrics.churnRatePct)}`}
        deltaPct={metrics.deltas.churnedTenants}
      />
    </ColumnLayout>
  );
}
